import { getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';


/**
 * Country list and phone validation for the public application form.
 *
 * Previously the form accepted phone numbers with no country awareness at
 * all - just "10 to 15 digits" - which is not a real rule for any country
 * and gave no way to validate or store numbers for applicants outside the US.
 * Since applicants can be from any country, the form has a country dropdown
 * next to each phone field, and libphonenumber-js validates against that
 * specific country's real numbering plan rather than guessing.
 *
 * Country names come from Intl.DisplayNames (built into the browser) rather
 * than a hand-maintained list, so there is no list of ~245 country names to
 * keep in sync by hand.
 */

const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

function countryName(code) {
  if (!regionNames) return code;
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

/**
 * All ISO 3166-1 alpha-2 country codes libphonenumber-js knows a numbering
 * plan for, sorted alphabetically by display name (not by code), since that
 * is how applicants actually scan a dropdown.
 */
export const COUNTRY_OPTIONS = getCountries()
  .map((code) => ({
    code,
    name: countryName(code),
    callingCode: getCountryCallingCode(code)
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const DEFAULT_PHONE_COUNTRY = 'US';

/**
 * Returns true if `value` is a real, dialable number for `country`. If the
 * applicant types a number starting with "+", that embedded country code
 * takes priority over the dropdown - matches how people actually paste
 * numbers they already have written down internationally.
 */
export function isValidPhoneForCountry(value, country) {
  if (!value || !country) return false;
  try {
    return isValidPhoneNumber(String(value), country);
  } catch {
    return false;
  }
}

/**
 * For editing an existing applicant's phone number. There is no
 * phone_country column on the applicants table, so the country has to be
 * recovered from the stored value itself.
 *
 * Applications submitted through the public form (after this feature
 * shipped) are stored in international format, e.g. "+1 415 555 2671" - the
 * "+1" lets libphonenumber-js identify the country with no guessing.
 *
 * Applicants from before this feature (or anyone who typed a number without
 * a "+") have no country marker in the stored string at all. There is no way
 * to recover their actual country from the digits alone, so this falls back
 * to the default. The admin doing the edit can pick the correct country from
 * the dropdown if that guess is wrong.
 */
export function guessPhoneCountry(value) {
  if (!value) return DEFAULT_PHONE_COUNTRY;
  try {
    const parsed = parsePhoneNumberFromString(String(value));
    return (parsed && parsed.country) || DEFAULT_PHONE_COUNTRY;
  } catch {
    return DEFAULT_PHONE_COUNTRY;
  }
}

